import type { PermissionModule } from '../lib/permissions';

export interface NavItem {
  label: string;
  to: string;
  wired: boolean;
  /** 新标签打开的站外/直达链接（渲染为 <a target=_blank> 而非 NavLink） */
  external?: boolean;
  phase?: 1 | 2 | 3;
  icon?: string;       // NAV_ICONS key
  countKey?: string;   // 对应 /api/counts 的字段
  hot?: boolean;       // 需要醒目标记（红色计数）
  module?: PermissionModule; // 用于按角色 view 权限过滤入口（概览等无模块入口可省略）
  /** 仅公司管理员可见。用于「公司财税资料」这类无 module 但受管理权限制的入口。 */
  adminOnly?: boolean;
}
export interface NavGroup {
  label: string;
  items: NavItem[];
  /**
   * 受后端环境开关约束的分组：`financeRead` = core-api 的 INTEGRAL_ENABLE_FINANCE_READ。
   * 门控关闭时（生产默认）整组不渲染 —— 声明一个点进去只会看到「未启用」的入口，
   * 对用户是噪音，也容易被误读成「功能已上线但坏了」。
   * 门控状态由 useFinanceGate() 提供（后端权限快照同源判定）。
   */
  gated?: 'financeRead';
}

// 侧栏图标：沿用冻结原型的 stroke 图标集（viewBox 0 0 24 24），并补充工序/仓库两枚。
export const NAV_ICONS: Record<string, string> = {
  grid: '<path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"/>',
  check: '<rect x="4" y="4" width="16" height="16" rx="3"/><path d="M9 12l2 2 4-4"/>',
  bell: '<path d="M18 15V10a6 6 0 1 0-12 0v5l-2 3h16z"/><path d="M10 21h4"/>',
  funnel: '<path d="M4 5h16l-6 7v6l-4 2v-8z"/>',
  users: '<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 8h5M18.5 5.5v5"/>',
  doc: '<path d="M6 3h8l4 4v14H6z"/><path d="M9 11h6M9 15h6"/>',
  sign: '<path d="M4 17c4-8 6 4 9-2s5 1 7-1"/><path d="M4 21h16"/>',
  cog: '<circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2"/>',
  layers: '<path d="M12 4 3 9l9 5 9-5z"/><path d="m3 15 9 5 9-5"/>',
  cart: '<circle cx="9" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/><path d="M3 4h2l2.5 11h11L21 8H6"/>',
  box: '<path d="M4 8l8-4 8 4v8l-8 4-8-4z"/><path d="M4 8l8 4 8-4M12 12v8"/>',
  truck: '<rect x="2" y="7" width="11" height="8"/><path d="M13 10h4l3 3v2h-7z"/><circle cx="6" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/>',
  shield: '<path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z"/><path d="m9 12 2 2 4-4"/>',
  undo: '<path d="M4 10h10a5 5 0 1 1 0 10H8"/><path d="m4 10 4-4M4 10l4 4"/>',
  wrench: '<path d="M15 4a5 5 0 0 0-6 6l-5 5 3 3 5-5a5 5 0 0 0 6-6l-3 3-2-2z"/>',
  receipt: '<path d="M6 3h12v18l-3-2-3 2-3-2-3 2z"/><path d="M9 8h6M9 12h6"/>',
  coin: '<circle cx="12" cy="12" r="8"/><path d="M9 10h5M9 13h5M11 7v10"/>',
  tag: '<path d="M4 4h8l8 8-8 8-8-8z"/><circle cx="9" cy="9" r="1.5"/>',
  percent: '<path d="M6 18 18 6"/><circle cx="7" cy="7" r="2"/><circle cx="17" cy="17" r="2"/>',
  puzzle: '<path d="M10 4h4v3a2 2 0 1 0 3 3h3v4h-3a2 2 0 1 0-3 3v3h-4v-3a2 2 0 1 0-3-3H4v-4h3a2 2 0 1 0 3-3z"/>',
  building: '<rect x="4" y="3" width="16" height="18"/><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2"/>',
  key: '<circle cx="8" cy="12" r="4"/><path d="M12 12h9M17 12v4M20 12v3"/>',
  log: '<path d="M5 3h14v18H5z"/><path d="M9 8h6M9 12h6M9 16h3"/>',
  ops: '<path d="M4 6h4v4H4zM16 14h4v4h-4z"/><path d="M8 8h6a2 2 0 0 1 2 2v4"/>',
  warehouse: '<path d="M3 21V10l9-5 9 5v11"/><path d="M7 21v-6h10v6"/>',
  bank: '<path d="M3 9l9-5 9 5"/><path d="M4 9h16M6 9v8M10 9v8M14 9v8M18 9v8"/><path d="M3 20h18"/>',
  flow: '<rect x="4" y="5" width="6" height="5" rx="1"/><rect x="14" y="14" width="6" height="5" rx="1"/><path d="M10 7.5h4a2 2 0 0 1 2 2V14"/>',
  powder: '<path d="M6 8h12l-1.2 12H7.2z"/><path d="M9 8V5h6v3"/><path d="M9 13h6"/>',
  globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3.6 3 14.4 0 18M12 3c-3 3.6-3 14.4 0 18"/>',
};

// 信息架构延续冻结原型：概览 / 销售 / 生产 / 采购 / 质量 / 财务 / 主数据与系统。
// wired=true 的入口已接入真实数据；其余为占位，按阶段逐步替换。
// countKey 命中的为 /api/counts 的真实行数，未命中或计数为 0 时不显示徽标。
export const NAV: NavGroup[] = [
  {
    label: '概览',
    items: [
      { label: '经营总览', to: '/', wired: true, icon: 'grid' },
    ],
  },
  {
    label: '销售',
    items: [
      { label: '客户', to: '/customers', wired: true, phase: 1, icon: 'users', countKey: 'customers', module: 'customers' },
      { label: '报价单', to: '/quotations', wired: true, phase: 1, icon: 'doc', countKey: 'quotations', module: 'quotations' },
      { label: '销售合同', to: '/contracts', wired: true, phase: 1, icon: 'sign', countKey: 'contracts', module: 'contracts' },
      { label: '交货单', to: '/deliveries', wired: true, phase: 1, icon: 'truck', countKey: 'deliveries', module: 'deliveries' },
    ],
  },
  {
    label: '生产',
    items: [
      { label: 'BOM 物料清单', to: '/boms', wired: true, phase: 2, icon: 'layers', countKey: 'boms', module: 'boms' },
      { label: '工艺模板', to: '/process-templates', wired: true, phase: 2, icon: 'flow', module: 'process-templates' },
      { label: '材料方案', to: '/material-plans', wired: true, phase: 2, icon: 'powder', module: 'material-plans' },
      { label: '生产工单', to: '/work-orders', wired: true, phase: 2, icon: 'cog', countKey: 'work_orders', module: 'work-orders' },
      // 受托客户工件：台账 / 溯源 / 来料备查 合并一页（避免三模块查同一件）
      { label: '送修工件', to: '/repair-ledger', wired: true, phase: 2, icon: 'clipboard', module: 'physical-assets' },
      { label: '工序', to: '/operations', wired: true, phase: 2, icon: 'ops', countKey: 'operations', module: 'operations' },
      { label: '库存', to: '/stock', wired: true, phase: 2, icon: 'box', module: 'stock/movements' },
      { label: '仓库', to: '/warehouses', wired: true, phase: 2, icon: 'warehouse', countKey: 'warehouses', module: 'warehouses' },
    ],
  },
  {
    label: '采购',
    items: [
      { label: '供应商', to: '/suppliers', wired: true, phase: 3, icon: 'building', countKey: 'suppliers', module: 'suppliers' },
      { label: '采购订单', to: '/purchase-orders', wired: true, phase: 3, icon: 'cart', countKey: 'purchase_orders', module: 'purchase-orders' },
      { label: '价目表', to: '/price-lists', wired: true, phase: 2, icon: 'tag', countKey: 'price_lists', module: 'price-lists' },
    ],
  },
  {
    label: '质量',
    items: [
      { label: '质检模板复核', to: '/qc-templates', wired: true, phase: 3, icon: 'check', module: 'qc-templates' },
      { label: '质检单', to: '/inspections', wired: true, phase: 3, icon: 'shield', countKey: 'inspections', module: 'inspections' },
      // 序列号查询已并入「送修工件 · 序列号」，此处不再单列入口
      { label: '返工', to: '/rework', wired: true, phase: 3, icon: 'undo', module: 'returns' },
    ],
  },
  {
    label: '售后',
    items: [
      { label: '售后返工', to: '/after-sales', wired: true, phase: 3, icon: 'wrench', module: 'returns' },
    ],
  },
  {
    label: '财务',
    gated: 'financeRead',
    items: [
      // ARCA 税票 / 收款：由 core-api 的 INTEGRAL_ENABLE_FINANCE_READ 门控。
      // 入口按模块 view 权限过滤（仅 admin/finance 可见）；未启用时页面给出「刻意未启用」说明。
      { label: 'ARCA 税票', to: '/arca-invoices', wired: true, phase: 3, icon: 'receipt', module: 'arca-invoices' },
      { label: '收款', to: '/payments', wired: true, phase: 3, icon: 'coin', module: 'payments' },
    ],
  },
  {
    label: '主数据与系统',
    items: [
      { label: '物料', to: '/items', wired: true, phase: 1, icon: 'puzzle', countKey: 'items', module: 'items' },
      // 公司财税资料属系统基础配置，仅公司管理员可维护（后端 PATCH /api/company 同样强制）。
    { label: '公司财税资料', to: '/company', wired: true, phase: 1, icon: 'bank', adminOnly: true },
      // 在线备份：仅公司管理员。后端同样强制 company_admin 人类会话，服务令牌被拒。
      // 逐系统独立备份，不宣称跨系统共同恢复点；页面不提供恢复入口。
    { label: '在线备份', to: '/system-backups', wired: true, phase: 1, icon: 'shield', adminOnly: true },
      // 员工账号管理：统一身份内的 Integral 员工账号（账号、角色、邀请、停用）。
      // 首版仅被显式授予 staff.manage 的账号可进入；与下面的 Portal 管理是两个
      // 相互独立的授权位，持有其一时不会获得另一份权限。
      { label: '员工账号', to: '/users', wired: true, phase: 3, icon: 'key', module: 'users' },
      // Portal 管理：客户账号、功能权限、管理员可访问客户范围，统一在 Integral 内集中管理。
      // 首版仅被显式授予 portal.manage 的账号可进入；由后端强制，前端不做兜底放行。
      { label: 'Portal 管理', to: '/portal-admin', wired: true, phase: 3, icon: 'shield', module: 'users' },
      // 网站管理：两个官网（西语 pinnaclelaser.com.ar / 中文 pinnalaser.com）的
      // CMS 后台统一入口。Integral 负责员工登录、权限与审计；CMS 负责页面、
      // 博客、媒体、发布。仅公司管理员可见；后台对每个写接口独立校验会话。
      { label: '网站管理', to: '/website-admin/open', wired: true, phase: 3, icon: 'globe', adminOnly: true, external: true },
    ],
  },
];

// 全局聚合搜索结果分组 key → 前端路由
export const SEARCH_KEY_ROUTE: Record<string, string> = {
  customers: '/customers',
  quotations: '/quotations',
  contracts: '/contracts',
  'work-orders': '/work-orders',
  items: '/items',
  inspections: '/inspections',
  invoices: '/arca-invoices',
};
